import { AppAnalytics, useAnalytics } from '@features/analytics'
import { AppDepsPlugins } from '@features/app-deps-create'
import { AppDepsProvider, useAppDeps } from '@features/app-deps-provider'
import { useAppStoreContext, useAuthClient, useRoute, useSetRoute } from '@features/app-hooks'
import { RequireLogin } from '@features/auth/require-login'
import { DebugPage } from '@features/dev/debug-page'
import { documentUploadsModule } from '@features/document-uploads'
import { ErrorReporting } from '@features/error-reporting'
import {
  OrganizationBrandingPage,
  OrganizationLoggedOutHeader,
  OrganizationLoginPage
} from '@features/organizations'
import { organizationModule } from '@features/organizations/organization-module'
import { sdkModule } from '@features/sdk-module'
import { STCreateFolderImportPage, STRunFolderImportPage } from '@features/st-folder-import'
import { STFolderListPage } from '@features/st-folder-list'
import { stFolderListFiltersModule } from '@features/st-folder-list/st-folder-list-filters-module'
import { STFolderViewer } from '@features/st-folder-viewer/st-folder-viewer'
import { OrganizationPlansPage } from '@features/st-organizations'
import { OrganizationAccountSettingsPage } from '@features/st-organizations/st-organization-account-settings-page'
import { STOrganizationMessageTemplatesPage } from '@features/st-organizations/st-organization-message-templates-page'
import { stOrganizationModule } from '@features/st-organizations/st-organization-module'
import { OrganizationIntegrationsPage } from '@features/st-organizations/st-organizations-integrations-page'
import { platformModule } from '@features/st-pdf-viewer/platform-module'
import { STQuestionnaire } from '@features/st-questionnaire'
import { DevModeProvider } from '@st/react-util/dev-mode'
import { Organization } from '@st/sdk'
import { ArchiveIcon, Button, FullPageCard, SnackbarContainer, useShowSnackbar } from '@st/theme'
import { ReactNode, useEffect, useMemo } from 'react'
import { match } from 'ts-pattern'
import { OrganizationBalancePage } from '~/features/billing/st-organization-balance-page'
import { OrganizationLoggedOutPlansPage } from '~/features/billing/st-organization-plans-page'
import { routes } from '~/features/routes'
import { ST_REDUX_STORE, StoreProvider, useProcess, useProcessState } from '~/store'
import { FullScreenNoticePage } from '~/theme'
import { upgradeLegacyId } from '~/util/legacy-id'
import DevPage from '../dev/dev-page'
import { ConnectAppPage } from '../st-organizations/st-connect-app-page'
import { OrganizationFolderTagsSettingsPage } from '../st-organizations/st-organization-folder-tags-settings-page'
import { OrganizationHeader, StanfordTaxHeader } from './pages/organization-login-page'
import { OrganizationRouteChangedPage } from './pages/organization-route-changed-page'
import { OrganizationSelectorPage } from './pages/organization-selector'
import { OrganizationSignUpPage } from './pages/organization-sign-up-page'
import { OrganizationTeamInvitationPage } from './pages/organization-team-invitation-page'
import { OrganizationTeamPage } from './pages/organization-team-page'
import { PageNotFound } from './pages/page-not-found'

/**
 * The app that accountants use to personalize their organization,
 * view the submissions of their clients, manage their billing, etc.
 */
export function OrganizationApp() {
  const appStoreContext = useAppStoreContext()
  const DEV_MODE = isDevMode()

  return (
    <StoreProvider store={ST_REDUX_STORE} context={appStoreContext}>
      <AppDepsProvider deps={appStoreContext}>
        <AppDepsPlugins />
        <AppAnalytics.Script />
        <ErrorReporting />

        <DevModeProvider value={DEV_MODE}>
          <SnackbarContainer>
            <OrganizationRoutes />
          </SnackbarContainer>
        </DevModeProvider>
      </AppDepsProvider>
    </StoreProvider>
  )
}

function isDevMode() {
  try {
    return localStorage.getItem('dev') === 'true'
  } catch (e) {
    return false
  }
}

function OrganizationRoutes() {
  const { sdk } = useAppDeps()
  const showSnackbar = useShowSnackbar()
  const { getToken } = useAuthClient()

  useProcess(platformModule, { showSnackbar })
  useProcess(sdkModule, { sdk })
  useProcess(documentUploadsModule)

  const setRoute = useSetRoute()
  const route = useRoute()

  // We want to know if the user was logged in at the point when the app was started
  // This is wrapped in useMemo to capture the initial value
  //
  // We can't use the "reactive" variable because this variable would trigger a re-render
  // and unexpectedly override the sign up flow right after they create an account
  const bootedAuthenticated = useMemo(() => {
    return getToken() !== undefined
  }, [getToken])

  switch (route?.name) {
    case '/':
    case 'login':
      return <OrganizationLoginPage header={<StanfordTaxHeader />} />
    case 'logged_out_pricing':
      return <OrganizationLoggedOutPlansPage />
    case 'logged_in_pricing':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationPlansPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'sign_up':
      // If the user was logged in at the point when the app was started,
      // we want to redirect them to the organization selector page
      // This prevents them from seeing the sign up page and creating another account
      if (bootedAuthenticated) {
        return <OrganizationSelectorPage />
      } else {
        return (
          <OrganizationSignUpPage
            header={<OrganizationLoggedOutHeader />}
            onSignedUp={(organization) =>
              setRoute({ name: 'folder_list', organizationSlug: organization.slug })
            }
          />
        )
      }
    case 'folder_list':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STFolderListPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'create_import':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STCreateFolderImportPage
            mode={route.preview ? 'preview' : 'normal'}
            onClose={() => setRoute({ name: 'folder_list' })}
            onImportSubmitted={(folderImport) => {
              setRoute({ name: 'run_import', importId: folderImport.id })
            }}
          />
        </OrganizationContainerRequiresLogin>
      )
    case 'run_import':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STRunFolderImportPage
            importId={route.importId}
            onClose={() => setRoute({ name: 'folder_list' })}
          />
        </OrganizationContainerRequiresLogin>
      )
    case 'branding':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationBrandingPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'team':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationTeamPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'team_invitation':
      return (
        <OrganizationTeamInvitationPage
          organizationSlug={route.organizationSlug}
          email={route.email}
        />
      )
    case 'organization_folder':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STFolderViewer folderId={route.folderId} />
        </OrganizationContainerRequiresLogin>
      )
    case 'debug':
      return <DebugPage />
    case 'dev':
      return <DevPage />
    case 'organization_folder_debug':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STFolderViewer folderId={route.folderId} debug={true} />
        </OrganizationContainerRequiresLogin>
      )
    case 'organization_preview_questionnaire':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STQuestionnaire preview={true} folderId={route.folderId} role="organization_member" />
        </OrganizationContainerRequiresLogin>
      )
    case 'account':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationAccountSettingsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'manage_organization_tags':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationFolderTagsSettingsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'manage_organization_message_templates':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STOrganizationMessageTemplatesPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'organization_balance':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationBalancePage />
        </OrganizationContainerRequiresLogin>
      )
    case 'manage_organization_integrations':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationIntegrationsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'connect_app':
      return (
        <ConnectAppPage
          clientId={route.client_id}
          state={route.state}
          redirectUri={route.redirect_uri}
          scopes={route.scope ? route.scope.split(' ') : []}
        />
      )
    case 'organization_folder_legacy':
      return (
        <RedirectTo
          href={
            routes.toPath({
              name: 'organization_folder',
              organizationSlug: route.organizationSlug,
              folderId: upgradeLegacyId(route.folderId)
            })!
          }
        />
      )
    case 'client_folder_legacy':
      return (
        <RedirectTo
          href={
            routes.toPath({
              name: 'client_folder',
              organizationSlug: route.organizationSlug,
              folderId: upgradeLegacyId(route.folderId)
            })!
          }
        />
      )
    case 'submission_list_legacy':
      return (
        <OrganizationRouteChangedPage
          newRoute={{ name: 'folder_list', organizationSlug: route.organizationSlug }}
        />
      )
    case 'workpaper_list_legacy':
      return (
        <OrganizationRouteChangedPage
          newRoute={{ name: 'folder_list', organizationSlug: route.organizationSlug }}
        />
      )

    default:
      return <PageNotFound />
  }
}

function OrganizationContainerRequiresLogin({
  children,
  organizationSlug
}: {
  children: ReactNode
  organizationSlug: string
}) {
  const analytics = useAnalytics()

  useProcess(organizationModule, { organizationSlug })
  const organization = useProcessState(organizationModule, (s) => s.organization)

  useEffect(() => {
    if (organization) analytics.setOrganization(organization)
  }, [analytics, organization])

  if (organization?.status == 'deactivated') {
    return <AccountDeactivatedNotice organization={organization} />
  }

  return (
    <RequireLogin
      loginScreen={
        <OrganizationLoginPage header={<OrganizationHeader organization={organization} />} />
      }
      loadingScreen={<></>}
    >
      {organization ? (
        <LoadOrganizationState organization={organization}>{children}</LoadOrganizationState>
      ) : null}
    </RequireLogin>
  )
}

function AccountDeactivatedNotice({ organization }: { organization: Organization }) {
  const { logout } = useAuthClient()
  return (
    <FullScreenNoticePage title="Account Deactivated" icon={<ArchiveIcon className="size-10" />}>
      <p className="max-w-2xl text-center">
        This account {organization.name} ({organization.slug}) is deactivated.
      </p>
      <p className="max-w-2xl text-center">
        If you would like to reactivate your account and restore your data, please contact us at
        support@stanfordtax.com.
      </p>

      <Button
        variant="default"
        className="mt-4"
        onClick={() => {
          logout()
          window.location.href = routes.toPath({ name: 'login' })!
        }}
      >
        Logout
      </Button>
    </FullScreenNoticePage>
  )
}

function LoadOrganizationState({
  organization,
  children
}: {
  organization: Organization
  children: ReactNode
}) {
  useProcess(stOrganizationModule, { organizationId: organization.id })

  // We require a reference to the stFolderListFiltersModule to ensure that filters are persistent
  // even when they navigate to different pages
  // If we don't do this, filters will get disposed every time someone navigates away from the folder list
  // to a different page.
  useProcess(stFolderListFiltersModule)

  const state = useProcessState(stOrganizationModule, (s) => s)

  return match(state)
    .with({ status: 'loading' }, () => null)
    .with({ status: 'loaded' }, () => <>{children}</>)
    .with({ status: 'unauthorized' }, () => (
      <FullPageCard>You are not authorized to access this organization.</FullPageCard>
    ))
    .exhaustive()
}

function RedirectTo({ href }: { href: string }) {
  useEffect(() => {
    window.location.href = href
  }, [href])
  return <></>
}
